import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { finalize, takeUntil } from 'rxjs'

import { AuthService } from '@app/modules/auth/auth.service'
import { DocumentService } from '../document.service'
import { DestroyService } from '@app/core/services/destroy.service'

import { DocumentInput, DocumentItem } from '../document.model'
import dayjs, { Dayjs } from 'dayjs'

@Component({
  selector: 'lin-document-record-form',
  templateUrl: './document-record-form.component.html',
  styleUrls: ['./document-record-form.component.scss'],
  providers: [DestroyService],
  standalone: false,
})
export class DocumentRecordFormComponent implements OnInit {
  form: FormGroup
  isRecordAdding = false

  @Input() document: DocumentItem
  @Input() documentSubject: 'driver' | 'vehicle' | 'asset'
  @Input() documentForm: FormGroup
  @Input() isEditMode = false
  @Input() isArchived = false
  @Input() prepareDocumentInfoData: DocumentInput

  @Output() recordAdded = new EventEmitter<DocumentItem>()

  get issueDate(): Dayjs {
    const dateFormat = this.authService.getCurrentUserDateFormat()
    return dayjs(<Dayjs>this.form?.get('issueDate')?.value, dateFormat).tz()
  }

  get issueDateIsValid(): boolean {
    return this.issueDate.isValid()
  }

  get isNotExpiring(): boolean {
    return !!this.form?.get('noExpiry')?.value
  }

  get expDateIsValid(): boolean {
    const dateFormat = this.authService.getCurrentUserDateFormat()
    const noExpiry = <boolean>this.form?.get('noExpiry')?.value
    const expDate = dayjs(<Dayjs>this.form?.get('expDate')?.value, dateFormat).tz()

    return noExpiry || (expDate.isValid() && expDate.isAfter(this.issueDate))
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly documentService: DocumentService,
    private readonly destroyService: DestroyService,
  ) {}

  ngOnInit(): void {
    this.initForm()
  }

  private initForm(): void {
    this.form = this.fb.group({
      issueDate: '',
      noExpiry: false,
      expDate: '',
      cost: ['', Validators.min(0)],
      note: '',
      files: '',
    })

    !this.isEditMode && this.form.disable()

    this.form.get('noExpiry').valueChanges.subscribe((noExpiry) => {
      !this.document && noExpiry ? this.documentForm.get('notifyBefore').disable() : this.documentForm.get('notifyBefore').enable()
    })
  }

  addRecord(): void {
    this.isRecordAdding = true
    const data = this.document ? this.prepareRecordData() : Object.assign(this.prepareRecordData(), this.prepareDocumentInfoData)
    const action$ = this.document ? this.documentService.edit(this.document.id, data) : this.documentService.create(data)

    action$
      .pipe(
        finalize(() => (this.isRecordAdding = false)),
        takeUntil(this.destroyService),
      )
      .subscribe((document) => {
        !this.document && this.redirectToCreatedDocument(document)
        this.recordAdded.emit(document)
        this.resetRecord()
      })
  }

  private prepareRecordData(): DocumentInput {
    /* eslint-disable */
    const formData = this.form.value
    const shouldNotNotify = this.document && formData.noExpiry

    let data = {
      issueDate: dayjs(formData.issueDate).tz().hour(0).minute(0).second(0).format(),
      noExpiry: formData.noExpiry,
      expDate: formData.noExpiry ? null : dayjs(formData.expDate).tz().hour(0).minute(0).second(0).format(),
      cost: formData.cost,
      note: formData.note,
      files: formData.files,
      ...(shouldNotNotify && { notifyBefore: 0 }),
    }

    return data as unknown as DocumentInput
    /* eslint-enable */
  }

  private resetRecord(): void {
    this.form.patchValue({
      issueDate: '',
      noExpiry: false,
      expDate: '',
      cost: '',
      note: '',
      files: '',
    })
  }

  private redirectToCreatedDocument(document: DocumentItem): void {
    switch (this.documentSubject) {
      case 'driver':
        void this.router.navigate(['client', 'drivers', document.driver.id, 'documents', document.id])
        break
      case 'vehicle':
        void this.router.navigate(['client', 'fleet', document.vehicle.id, 'documents', document.id])
        break
      case 'asset':
        void this.router.navigate(['client', 'assets', 'documents', document.id])
        break
    }
  }

  isSubmitDocumentDisabled(): boolean {
    const title = <string>this.documentForm.get('title').value
    const hasSubject = this.documentForm.get(this.documentSubject).valid

    return !this.issueDateIsValid || !this.expDateIsValid || !title || this.isArchived
    //|| (!this.document && !hasSubject)
  }
}
